import { createContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { DEFAULT_IMAGES } from '../constants/defaults';
import { preloadImage } from '../utils/imageCache';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    let unsubscribeFirestore = null;
    let isFirstLoad = true;

    // Listener de autenticación
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setInitializing(false); // Auth check completado
      
      if (user) {
        // Listener en tiempo real de Firestore
        const userDocRef = doc(db, 'users', user.uid);
        
        unsubscribeFirestore = onSnapshot(
          userDocRef,
          async (docSnap) => {
            if (docSnap.exists()) {
              // Datos de Firestore disponibles
              const firestoreData = docSnap.data();
              // photoURL: si es null (Premium expirado, foto eliminada) no hacer fallback
              const photoURL = firestoreData.photoURL === null || firestoreData.photoURL === ''
                ? null
                : (firestoreData.photoURL || firestoreData.profileImage || user.photoURL || DEFAULT_IMAGES.PROFILE);
              // URL efectiva del banner: si bannerURL es null o '' (tras eliminar), no usar legacy
              const effectiveBannerURL = (firestoreData.bannerURL != null && firestoreData.bannerURL !== '')
                ? firestoreData.bannerURL
                : (firestoreData.bannerURL === undefined && typeof firestoreData.banner === 'string' && firestoreData.banner.startsWith('http') ? firestoreData.banner : null);

              const completeData = {
                id: user.uid,
                email: user.email,
                displayName: user.displayName || firestoreData.displayName || 'Usuario',
                photoURL,
                ...firestoreData,
                blocked: Array.isArray(firestoreData.blocked) ? firestoreData.blocked : [],
                bannerURL: effectiveBannerURL,
                savedPosts: Array.isArray(firestoreData.savedPosts) ? firestoreData.savedPosts : [],
                savedServices: Array.isArray(firestoreData.savedServices) ? firestoreData.savedServices : [],
                photoURLIsGif: firestoreData.photoURLIsGif === true,
                bannerURLIsGif: firestoreData.bannerURLIsGif === true,
                profileBackgroundType: firestoreData.profileBackgroundType || null,
                profileBackgroundURL: firestoreData.profileBackgroundURL || null,
                recentAvatarURLs: Array.isArray(firestoreData.recentAvatarURLs) ? firestoreData.recentAvatarURLs : [],
                recentBannerURLs: Array.isArray(firestoreData.recentBannerURLs) ? firestoreData.recentBannerURLs : [],
                premiumNameColor: firestoreData.premiumNameColor || null
              };
              
              // Precargar foto si es la primera carga
              if (isFirstLoad && photoURL) {
                await preloadImage(photoURL).catch(() => {});
              }
              
              // Establecer datos completos
              setUserData(completeData);
              
              // Terminar loading solo en la primera carga
              if (isFirstLoad) {
                isFirstLoad = false;
                setLoading(false);
              }
            } else {
              // Documento no existe, crear datos básicos
              const basicData = {
                id: user.uid,
                email: user.email,
                displayName: user.displayName || 'Usuario',
                photoURL: user.photoURL || DEFAULT_IMAGES.PROFILE,
                bio: '',
                postsCount: 0,
                followersCount: 0,
                followingCount: 0,
                blocked: [],
                savedPosts: [],
                savedServices: [],
                premiumNameColor: null
              };
              
              if (isFirstLoad && basicData.photoURL) {
                await preloadImage(basicData.photoURL).catch(() => {});
              }
              
              setUserData(basicData);
              
              if (isFirstLoad) {
                isFirstLoad = false;
                setLoading(false);
              }
            }
          },
          (error) => {
            console.error('Error al escuchar cambios del usuario:', error);
            
            // Fallback: usar datos de Auth
            const fallbackData = {
              id: user.uid,
              email: user.email,
              displayName: user.displayName || 'Usuario',
              photoURL: user.photoURL || DEFAULT_IMAGES.PROFILE,
              bio: '',
              postsCount: 0,
              followersCount: 0,
              followingCount: 0,
              blocked: [],
              savedPosts: [],
              savedServices: [],
              premiumNameColor: null
            };
            
            setUserData(fallbackData);
            
            if (isFirstLoad) {
              isFirstLoad = false;
              setLoading(false);
            }
          }
        );
      } else {
        setUserData(null);
        setLoading(false);
      }
    });

    // Cleanup
    return () => {
      unsubscribeAuth();
      if (unsubscribeFirestore) {
        unsubscribeFirestore();
      }
    };
  }, []);

  const value = {
    currentUser,
    userData,
    loading,
    initializing
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
